<script>
import AnnotateMixin from "@/mixins/AnnotateMixin";
import Annotation from "@/classes/annotation";
import CurrentUserMixin from "@/mixins/CurrentUserMixin.vue";
import createSocket from '@/utils/socket';
const { v4: uuidV4 } = require('uuid');

export default {
  mixins: [AnnotateMixin, CurrentUserMixin],
  data() {
    return {
      sam3_enabled: false,
      sam3_loading: false,
      sam3_query: '',
      sam3_status: null,
      sam3_socket: null,
      sam3_socket_token: null,
      sam3_label_hint: null,
    };
  },
  async mounted() {
    this.sam3_enabled = await this.$api.user_setting.get_user_setting_and_convert_to_bool('SettingEnableSegAny');
  },
  beforeUnmount() {
    this.teardownSam3Socket();
  },
  computed: {
    isSam3Loading() {
      return this.sam3_loading && this.sam3_enabled;
    },
  },
  methods: {
    async canvas_mousedown(details) {
      if (!this.sam3_enabled || this.sam3_loading || this.annotationLimitReached) {
        return;
      }
      const x = details.normalized_x != null ? details.normalized_x : details.original_x;
      const y = details.normalized_y != null ? details.normalized_y : details.original_y;
      if (x == null || y == null) {
        return;
      }
      const query = (this.sam3_query || '').trim();
      await this.runSam3Segment('point', { x, y, prompt: query || null });
    },
    async runSam3TextSegment() {
      const prompt = (this.sam3_query || '').trim();
      if (!prompt) {
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('annotate.sam3_no_prompt'));
        return;
      }
      await this.runSam3Segment('text', { prompt });
    },
    async runSam3Segment(prompt_type, { prompt = null, x = null, y = null } = {}) {
      const assetId = this.annotate_asset_id || this.asset_id;
      if (!assetId) {
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('annotate.sam3_error'));
        return;
      }
      const labelHint = (prompt || '').trim() || (this.sam3_query || '').trim() || null;
      const apiPrompt = prompt_type === 'text' ? prompt : labelHint;
      this.sam3_loading = true;
      this.sam3_status = this.$t('annotate.sam3_running');
      this.sam3_label_hint = labelHint;
      const token = uuidV4();
      this.sam3_socket_token = token;
      this.setupSam3Socket(token, labelHint);
      try {
        const result = await this.$api.sam3.segment(assetId, prompt_type, {
          prompt: apiPrompt,
          x,
          y,
          websocketsToken: token,
        });
        if (!result) {
          this.finishSam3WithError(this.$t('annotate.sam3_error'));
          return;
        }
        if (result.error) {
          this.finishSam3WithError(String(result.error));
        }
      } catch (error) {
        console.error(error);
        this.finishSam3WithError(this.$t('annotate.sam3_error'));
      }
    },
    finishSam3WithError(message) {
      this.sam3_loading = false;
      this.sam3_status = this.$t('annotate.sam3_error');
      this.teardownSam3Socket();
      this.$store.dispatch('snackbars/add_failed_snack', String(message));
    },
    teardownSam3Socket() {
      if (this.sam3_socket) {
        this.sam3_socket.close();
      }
      this.sam3_socket = null;
      this.sam3_socket_token = null;
      this.sam3_label_hint = null;
    },
    setupSam3Socket(token, labelHint) {
      this.teardownSam3Socket();
      this.sam3_socket_token = token;
      this.sam3_label_hint = labelHint;
      const socket = createSocket();
      this.sam3_socket = socket;
      socket.on('connect', () => {
        socket.emit('subscribe', token);
      });
      socket.on('message', (message) => {
        let payload = null;
        try {
          payload = JSON.parse(JSON.parse(message));
        } catch (e) {
          return;
        }
        if (!payload || payload.type !== 'sam3') {
          return;
        }
        if (payload.status === 'processing' && payload.message) {
          this.sam3_status = String(payload.message);
          return;
        }
        if (payload.status === 'error') {
          this.finishSam3WithError(String(payload.error || this.$t('annotate.sam3_error')));
          return;
        }
        if (payload.status === 'completed') {
          this.sam3_loading = false;
          const hint = labelHint || payload.prompt || this.sam3_query;
          this.applySam3Boxes(payload.boxes, hint);
          this.teardownSam3Socket();
        }
      });
    },
    applySam3Boxes(boxes, defaultLabel) {
      if (!Array.isArray(boxes) || boxes.length === 0) {
        this.sam3_status = this.$t('annotate.sam3_no_boxes');
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('annotate.sam3_no_boxes'));
        return;
      }
      const color = [0, 180, 0];
      let added = 0;
      for (let i = 0; i < boxes.length; i += 1) {
        const box = boxes[i];
        if (!box || !Array.isArray(box.coords) || box.coords.length !== 4) {
          continue;
        }
        const promptLabel = defaultLabel != null ? String(defaultLabel).trim() : '';
        const serverLabel = box.label != null ? String(box.label).trim() : '';
        const label = promptLabel
          || (serverLabel && serverLabel !== 'seg_any_bbox' ? serverLabel : 'seg_any_bbox');
        const score = box.score != null ? String(box.score) : '0.9';
        const annotation = new Annotation(uuidV4(), 'box', box.coords, score, label, color, true);
        this.addAnnotationClearSelection(annotation);
        added += 1;
      }
      if (added === 0) {
        this.sam3_status = this.$t('annotate.sam3_no_boxes');
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('annotate.sam3_no_boxes'));
        return;
      }
      this.sam3_status = this.$t('annotate.sam3_added', { count: added });
      this.$store.dispatch('snackbars/add_success_snack', this.sam3_status);
    },
  },
};
</script>
